/**
 * Client fuer die Seite "Aktivitaeten" (Phase 1): `GET /api/activities` (Liste, Stueck fuer Stueck
 * ueber eine Fortsetzungsmarke), `GET /api/activities/filter-optionen` und `GET /api/activities/export`
 * (CSV mit denselben Filtern).
 *
 * Die Typen sind die woertliche Spiegelung der Backend-Antwort - EIGENSTAENDIG hier notiert statt
 * importiert, wie es auch die uebrigen `api/*.ts`-Module halten. Wer, Quelle, Bereich und die
 * Anzeigeform der Werte (`altAnzeige`/`neuAnzeige`) leitet der Server ab; die Seite zeigt sie nur.
 *
 * Die fruehere Kachel "Letzte Aktivitaeten" der Uebersicht (`GET /api/activities/recent`, in
 * `api/historie.ts`) ist mit Task 9 entfallen; `api/historie.ts` fuehrt nur noch den Zeitstrahl EINER
 * Bestellung.
 */
import { anfrage, ladeUndSpeichereDatei } from "./basis.ts";

export type BereichKey =
  | "status"
  | "termine"
  | "transport"
  | "preise"
  | "zahlungen"
  | "dokumente"
  | "positionen"
  | "aufgaben"
  | "stammdaten"
  | "fx"
  | "objektstammdaten"
  | "verwaltung"
  | "sonstiges";

/** Quelle EINES Eintrags. */
export type AktivitaetQuelle = "tool" | "tool_jtl" | "jtl" | "dsv" | "system" | "migration";

/** Quelle als FILTERwert; `tool` trifft serverseitig auch `tool_jtl`. */
export type QuellenFilter = "tool" | "jtl" | "dsv" | "system" | "migration";

/**
 * Die Art des Objekts einer Zeile (Spiegel von `Wo.art`, `backend/src/domaene/aktivitaeten.ts`): Bestellung und Sendung
 * kommen aus den Hub-Quellen, jede andere Art steht fuer ein Objekt des Aenderungsprotokolls - dessen `kennung` ist die
 * `objekt_id`, den Absprung je Art entscheidet die Seite (`AktivitaetenPage/aktivitaetenAnzeige.ts#klickZiel`).
 */
export type WoArt =
  | "bestellung"
  | "sendung"
  | "fx_konto"
  | "fx_deal"
  | "fx_zuordnung"
  | "zahlungsvorlage"
  | "lieferant"
  | "zustaendigkeit"
  | "aufgabenvorlage"
  | "nutzer"
  | "einstellung";

/** Werte des Wo-Filters; `vorlage` steht fuer Zahlungs- und Aufgabenvorlagen sowie Zustaendigkeiten. */
export type WoFilter = "bestellung" | "sendung" | "fx" | "lieferant" | "vorlage" | "verwaltung";

export interface AktivitaetWo {
  art: WoArt;
  kennung: string;
  bezeichnung: string | null;
  lieferant: string | null;
  oeffenbar: boolean;
  /**
   * Die Bestellung wurde ueber den Hub geloescht (es gibt einen Loescheintrag): `bezeichnung` traegt dann schon den Zusatz
   * " (gelöscht)", `oeffenbar` ist false. Ohne Eintrag ist eine nicht oeffenbare Bestellung "In JTL gelöscht".
   */
  geloescht: boolean;
  orderId?: string | null;
  jtlOrderId?: number | null;
}

export interface Aktivitaet {
  kennung: string;
  ts: string;
  aktor: string;
  aktorSchluessel: string;
  aktorArt: "person" | "automatisch";
  wo: AktivitaetWo;
  feld: string | null;
  feldLabel: string | null;
  feldTyp: string;
  bereich: BereichKey;
  altWert: string | null;
  neuWert: string | null;
  altAnzeige: string | null;
  neuAnzeige: string | null;
  quelle: AktivitaetQuelle;
  summary: string | null;
  betroffene: unknown;
}

export interface AktivitaetenAntwort {
  eintraege: Aktivitaet[];
  /** Fortsetzungsmarke fuer das naechste Stueck; `null` = keine weiteren Eintraege. */
  nach: string | null;
  /** Nur im ersten Stueck (ohne `nach`) gezaehlt, sonst `null`. */
  gesamt: number | null;
  /** `true`: der Server hat nur bis 10.001 gezaehlt ("mehr als 10.000 Aenderungen"). */
  gesamtIstUntergrenze: boolean;
  jtlSucheAusgefallen: boolean;
  jtlSucheGekappt: boolean;
}

/**
 * Die Filter der Seite. Die Reihenfolge der Felder ist zugleich die Reihenfolge der Schluessel im
 * Abfragetext (`aktivitaetenAbfrageText`).
 */
export interface AktivitaetenAbfrage {
  /** `YYYY-MM-DD`, Berliner Tag. */
  von?: string;
  bis?: string;
  wer?: string[];
  bereich?: BereichKey[];
  feld?: string[];
  wo?: WoFilter[];
  quelle?: QuellenFilter[];
  q?: string;
  sort?: "desc" | "asc";
}

export interface Auswahl {
  key: string;
  label: string;
}

export interface AktivitaetenFilterOptionen {
  personen: Auswahl[];
  automatisch: Auswahl[];
  bereiche: { key: BereichKey; label: string; felder: Auswahl[] }[];
  /** Die Objektgruppen des Wo-Filters (ohne "Nutzer & Einstellungen", wenn dem Nutzer beide Rechte fehlen). */
  wo: { key: WoFilter; label: string }[];
  quellen: Auswahl[];
}

/** Anzeigename je Quelle (Spalte "Quelle" der Seite). */
export const QUELLEN_LABEL: Record<AktivitaetQuelle, string> = {
  tool: "Tool",
  tool_jtl: "Tool → JTL",
  jtl: "JTL",
  dsv: "DSV",
  system: "System",
  migration: "Migration",
};

/**
 * Der Abfragetext (`URLSearchParams`, ohne fuehrendes `?`) zu einer Abfrage und einer
 * Fortsetzungsmarke. Listen werden wiederholt (`wer=a&wer=b`), leere Werte (`undefined`, `""`, leere
 * Listen, `null`) entfallen. Schluesselreihenfolge: von, bis, wer, bereich, feld, wo, quelle, q, sort,
 * nach - damit der Text stabil und vergleichbar bleibt.
 */
export function aktivitaetenAbfrageText(
  abfrage: AktivitaetenAbfrage,
  nach?: string | null,
): string {
  const p = new URLSearchParams();
  const einzel = (schluessel: string, wert: string | null | undefined): void => {
    if (wert) p.append(schluessel, wert);
  };
  const liste = (schluessel: string, werte: readonly string[] | undefined): void => {
    for (const wert of werte ?? []) einzel(schluessel, wert);
  };
  einzel("von", abfrage.von);
  einzel("bis", abfrage.bis);
  liste("wer", abfrage.wer);
  liste("bereich", abfrage.bereich);
  liste("feld", abfrage.feld);
  liste("wo", abfrage.wo);
  liste("quelle", abfrage.quelle);
  einzel("q", abfrage.q);
  einzel("sort", abfrage.sort);
  einzel("nach", nach);
  return p.toString();
}

/** `pfad` plus `?text`; bei leerem Text ohne Fragezeichen. */
function mitText(pfad: string, text: string): string {
  return text ? `${pfad}?${text}` : pfad;
}

/**
 * Ein Stueck der Liste (`GET /api/activities`). `nach` ist die Marke aus der Antwort des vorigen
 * Stuecks, `null` fuer das erste.
 */
export function aktivitaetenLaden(
  abfrage: AktivitaetenAbfrage,
  nach: string | null,
  signal?: AbortSignal,
): Promise<AktivitaetenAntwort> {
  return anfrage<AktivitaetenAntwort>(
    mitText("/api/activities", aktivitaetenAbfrageText(abfrage, nach)),
    { signal },
  );
}

/** Die Auswahllisten der Filterleiste (`GET /api/activities/filter-optionen`). */
export function aktivitaetenFilterOptionenLaden(
  signal?: AbortSignal,
): Promise<AktivitaetenFilterOptionen> {
  return anfrage<AktivitaetenFilterOptionen>("/api/activities/filter-optionen", { signal });
}

/**
 * Die CSV zur Abfrage als Datei speichern (`GET /api/activities/export`, ohne Fortsetzungsmarke -
 * der Export umfasst alle Treffer). Dateiname `aktivitaeten_<von|alle>_<bis|alle>.csv`. Loest bei
 * Erfolg ohne Wert auf und wirft bei Fehlstatus/Netzfehler (siehe `ladeUndSpeichereDatei`).
 */
export function aktivitaetenExportieren(abfrage: AktivitaetenAbfrage): Promise<void> {
  const dateiname = `aktivitaeten_${abfrage.von || "alle"}_${abfrage.bis || "alle"}.csv`;
  return ladeUndSpeichereDatei(
    mitText("/api/activities/export", aktivitaetenAbfrageText(abfrage)),
    dateiname,
  );
}
